import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, ArrowLeft, Phone, MessageSquare, ShieldCheck, Clock, User } from 'lucide-react';
import { siteConfig } from '../data';
import { formatDanishDate } from '../lib/utils';

export default function VidensbankDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        let res;
        try {
          res = await fetch('/_api/public/content/vidensbank_articles');
          if (!res.ok) throw new Error('not ok');
        } catch {
          res = await fetch('https://api.dev.auxo.dk/public/content/vidensbank_articles');
        }
        if (!res.ok) {
          throw new Error('Kunne ikke hente artikler fra CMS');
        }
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.items || [];
        list.sort((a, b) => (b.published_date || '').localeCompare(a.published_date || ''));
        setArticles(list);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const getSlug = (item) => {
    if (!item.slug) return '';
    return typeof item.slug === 'object' ? item.slug._ : item.slug;
  };

  const currentArticle = articles.find((a) => getSlug(a) === slug);
  const relatedArticles = articles.filter((a) => getSlug(a) !== slug).slice(0, 2);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-slate-50">
        <div className="w-12 h-12 border-4 border-blue-100 border-t-[#103B66] rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-medium">Henter artiklen...</p>
      </div>
    );
  }

  if (error || !currentArticle) {
    return (
      <div className="bg-slate-50 min-h-[60vh] flex items-center justify-center px-4">
        <div className="bg-white rounded-none border border-slate-100 p-8 text-center max-w-md shadow-sm">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-none flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 rotate-180" />
          </div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">Artiklen blev ikke fundet</h3>
          <p className="text-slate-500 mb-6">
            Siden du leder efter findes desværre ikke, eller er blevet flyttet.
          </p>
          <Link
            to="/vidensbank"
            className="inline-flex items-center px-5 py-2.5 bg-[#103B66] text-white font-semibold rounded-none hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Gå til Vidensbanken
          </Link>
        </div>
      </div>
    );
  }

  const imageUrl = currentArticle.image?.[0] || 'https://assets.auxodev.dk/assets/2cadb5d8-c2c5-4850-ae22-0167799adce5/6fa6bf9e-bc8b-4301-b93b-b252cb898cf6.jpg';

  return (
    <div className="bg-slate-50 min-h-screen py-12 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Back Button */}
        <div className="mb-8">
          <Link
            to="/vidensbank"
            className="inline-flex items-center text-slate-600 hover:text-[#103B66] font-semibold transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tilbage til Vidensbanken
          </Link>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          
          {/* Article Content Column */}
          <div className="lg:col-span-2">
            <article className="bg-white rounded-none border border-slate-100 shadow-sm overflow-hidden p-6 md:p-10">
              
              {/* Category & Date */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
                <span className="bg-blue-50 text-[#103B66] font-bold px-3 py-1 rounded-none">
                  {currentArticle.category}
                </span>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1.5" />
                  {formatDanishDate(currentArticle.published_date)}
                </span>
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1.5" />
                  5 min. læsning
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#103B66] tracking-tight mb-8 leading-tight">
                {currentArticle.title}
              </h1>

              {/* Large Image */}
              <div className="aspect-video w-full rounded-none overflow-hidden mb-10 bg-slate-100">
                <img 
                  src={imageUrl} 
                  alt={currentArticle.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Rich Text Content */}
              <div 
                className="text-slate-700 text-lg leading-relaxed space-y-6 
                  [&>p]:mb-6 [&>p]:leading-relaxed 
                  [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2
                  [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2
                  [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-[#103B66] [&>h2]:mt-10 [&>h2]:mb-4 
                  [&>h3]:text-xl [&>h3]:font-bold [&>h3]:text-[#103B66] [&>h3]:mt-8 [&>h3]:mb-3
                  [&>strong]:font-bold [&>strong]:text-slate-900"
                dangerouslySetInnerHTML={{ __html: currentArticle.content }}
              />

            </article>
          </div>

          {/* Sidebar Column */}
          <div className="space-y-8">
            
            {/* Author / Contact Card */}
            <div className="bg-white rounded-none border border-slate-100 shadow-sm p-6 md:p-8 text-center">
              <div className="w-20 h-20 bg-blue-50 text-[#103B66] rounded-none flex items-center justify-center mx-auto mb-4 border border-blue-100">
                <User className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-[#103B66] mb-1">Online Tryghed</h3>
              <p className="text-slate-500 text-sm mb-6">Din personlige rådgiver i Slagelse og omegn</p>
              
              <div className="space-y-3">
                <a
                  href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                  className="flex items-center justify-center w-full py-3 bg-[#103B66] text-white font-bold rounded-none hover:bg-slate-800 transition-all shadow-sm"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Ring {siteConfig.phone}
                </a>
                <a
                  href={`sms:${siteConfig.sms.replace(/\s/g, '')}`}
                  className="flex items-center justify-center w-full py-3 bg-slate-100 text-slate-700 font-bold rounded-none hover:bg-slate-200 transition-all"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send en SMS
                </a>
              </div>
            </div>

            {/* Tryghedssamtale Info Card */}
            <div className="bg-gradient-to-br from-[#103B66] to-slate-900 rounded-none p-6 md:p-8 text-white shadow-lg relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_50%)]" />
              <div className="relative z-10">
                <span className="inline-block bg-blue-600/60 text-white text-xs font-bold px-2.5 py-1 rounded-none mb-4 uppercase tracking-wider">
                  Kom godt i gang
                </span>
                <h4 className="text-xl font-bold mb-3">
                  Første telefonkonsultation til kun 99,-
                </h4>
                <p className="text-slate-200 text-sm mb-4 leading-relaxed">
                  Få en personlig samtale på ca. 10-15 minutter, hvor vi vejleder dig om dine spørgsmål og sikring mod svindel og datalæk.
                </p>
                <p className="text-xs text-blue-200/90 mb-6 leading-relaxed">
                  Vi opretter dig i vores system ved første samtale, så vi altid kan se din historik, hvis du ringer igen.
                </p>
                <a
                  href={`tel:${siteConfig.phone.replace(/\s/g, '')}`}
                  className="block text-center w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-none transition-all shadow-md"
                >
                  Book samtale nu
                </a>
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className="bg-white rounded-none border border-slate-100 shadow-sm p-6 md:p-8">
                <h4 className="text-lg font-bold text-[#103B66] mb-6">Andre læser også</h4>
                <div className="space-y-6">
                  {relatedArticles.map((article) => {
                    const rSlug = getSlug(article);
                    const rImageUrl = article.image?.[0] || 'https://assets.auxodev.dk/assets/2cadb5d8-c2c5-4850-ae22-0167799adce5/6fa6bf9e-bc8b-4301-b93b-b252cb898cf6.jpg';
                    
                    return (
                      <Link 
                        key={article.id} 
                        to={`/vidensbank/${rSlug}`}
                        className="flex gap-4 group"
                      >
                        <div className="w-20 h-20 rounded-none overflow-hidden bg-slate-100 flex-shrink-0">
                          <img 
                            src={rImageUrl} 
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <div className="flex-grow min-w-0">
                          <span className="text-xs font-bold text-blue-600 block mb-1">
                            {article.category}
                          </span>
                          <h5 className="font-bold text-sm text-slate-800 group-hover:text-blue-600 transition-colors line-clamp-2 leading-snug">
                            {article.title}
                          </h5>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

          </div>

        </div>

      </div>
    </div>
  );
}
